import { RateLimiter } from './rate-limiter';

export interface FetchAllPagesPageInfo {
    page: number;
    total: number;
    loaded: number;
}

export interface FetchAllPagesOptions<T> {
    pageSize?: number;
    concurrency?: number;
    maxRequestsPerMinute?: number;
    /** Optional shared limiter (e.g. across multiple listing passes). */
    rateLimiter?: RateLimiter;
    /** Fetch a single page (0-indexed). Must return items + total for that query. */
    fetchPage: (page: number, pageSize: number) => Promise<{ items: T[]; total: number }>;
    /**
     * Called immediately before each `fetchPage` (including page 0).
     * Use to re-assert request grouping / auth context right before the network call.
     */
    beforeFetchPage?: (page: number) => void | Promise<void>;
    getId?: (item: T) => string | undefined;
    onProgress?: (loaded: number, total: number) => void;
    signal?: AbortSignal;
    /**
     * Called in page order (0, 1, 2…) as soon as each page can be delivered.
     * Prefer converting/persisting here and discarding items to keep peak RAM low.
     */
    onPage?: (items: T[], info: FetchAllPagesPageInfo) => void | Promise<void>;
    /**
     * When false (default if onPage is provided), do not retain items in the returned array.
     * Set true only if the caller still needs the full list.
     */
    retainItems?: boolean;
}

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_CONCURRENCY = 5;
const DEFAULT_MAX_REQUESTS_PER_MINUTE = 100;

const throwIfAborted = (signal?: AbortSignal): void => {
    if (signal?.aborted) {
        throw new DOMException('Export cancelled', 'AbortError');
    }
};

/**
 * Fetch every page of a paginated listing with bounded concurrency and a
 * sliding-window rate limit (default: 5 concurrent, ≤100 requests/min, page size 100).
 *
 * Pages are delivered via `onPage` in page order. With `retainItems: false`, peak
 * memory stays near the in-flight window instead of the full dataset.
 */
export async function fetchAllPagesWithRateLimit<T>(options: FetchAllPagesOptions<T>): Promise<T[]> {
    const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
    const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
    const maxRequestsPerMinute = options.maxRequestsPerMinute ?? DEFAULT_MAX_REQUESTS_PER_MINUTE;
    const getId = options.getId ?? ((item: T) => (item as { id?: string })?.id);
    const { fetchPage, beforeFetchPage, onProgress, signal, onPage } = options;
    const retainItems = options.retainItems ?? !onPage;

    const runFetchPage = async (page: number, size: number) => {
        await beforeFetchPage?.(page);
        return fetchPage(page, size);
    };

    throwIfAborted(signal);

    const limiter =
        options.rateLimiter ??
        new RateLimiter({ maxRequests: maxRequestsPerMinute, windowMs: 60_000 });

    await limiter.acquire(signal);
    const first = await runFetchPage(0, pageSize);
    const total = first.total || 0;
    const pageCount = total <= 0 ? 0 : Math.ceil(total / pageSize);

    const seenIds = new Set<string>();
    const retained: T[] = [];
    let loaded = 0;

    const takeUnique = (items: T[]): T[] => {
        const unique: T[] = [];
        for (const item of items) {
            const id = getId(item);
            if (id) {
                if (seenIds.has(id)) {
                    continue;
                }
                seenIds.add(id);
            }
            unique.push(item);
        }
        return unique;
    };

    const deliverPage = async (page: number, rawItems: T[]): Promise<void> => {
        const items = takeUnique(rawItems);
        loaded += items.length;
        onProgress?.(loaded, total);
        await onPage?.(items, { page, total, loaded });
        if (retainItems) {
            retained.push(...items);
        }
    };

    if (pageCount === 0) {
        onProgress?.(0, 0);
        return retained;
    }

    await deliverPage(0, first.items);

    if (pageCount <= 1) {
        return retained;
    }

    // Slot per page; cleared after ordered emit to free memory.
    const slots: Array<T[] | undefined> = new Array(pageCount);
    let nextFetchPage = 1;
    let nextEmitPage = 1;
    let fetchError: unknown = null;

    const waiters: Array<() => void> = [];
    const notify = (): void => {
        while (waiters.length > 0) {
            waiters.shift()!();
        }
    };
    const wait = (): Promise<void> =>
        new Promise(resolve => {
            waiters.push(resolve);
        });

    const workers = Array.from({ length: Math.min(concurrency, pageCount - 1) }, async () => {
        while (true) {
            throwIfAborted(signal);
            if (fetchError) {
                return;
            }

            // Sliding window: do not run too far ahead of emit (bounds buffered pages).
            while (nextFetchPage < pageCount && nextFetchPage >= nextEmitPage + concurrency) {
                throwIfAborted(signal);
                if (fetchError) {
                    return;
                }
                await wait();
            }

            const page = nextFetchPage;
            if (page >= pageCount) {
                return;
            }
            nextFetchPage += 1;

            try {
                await limiter.acquire(signal);
                throwIfAborted(signal);
                const result = await runFetchPage(page, pageSize);
                slots[page] = result.items;
                notify();
            } catch (err) {
                fetchError = err;
                notify();
                throw err;
            }
        }
    });

    const workersDone = Promise.all(workers).catch(err => {
        fetchError = fetchError || err;
        notify();
    });

    while (nextEmitPage < pageCount) {
        throwIfAborted(signal);
        if (fetchError) {
            throw fetchError;
        }

        while (slots[nextEmitPage] === undefined) {
            throwIfAborted(signal);
            if (fetchError) {
                throw fetchError;
            }
            await wait();
        }

        const items = slots[nextEmitPage]!;
        slots[nextEmitPage] = undefined;
        const page = nextEmitPage;
        nextEmitPage += 1;
        notify(); // unblock fetch window
        await deliverPage(page, items);
    }

    await workersDone;
    if (fetchError) {
        throw fetchError;
    }

    return retained;
}
